import { format } from 'date-fns'

import { supabase } from './supabase'
import type { HealthEntry, HealthEntryInput } from '@/types/health'

export const getTodayDate = () => format(new Date(), 'yyyy-MM-dd')

export const parseListInput = (value: string) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

export const formatListInput = (items?: string[] | null) => (items ?? []).join(', ')

export const calculateHealthScore = (entry: Partial<HealthEntry> | null) => {
  if (!entry) {
    return null
  }

  const metrics: number[] = []

  if (typeof entry.sleep_quality === 'number') {
    metrics.push(entry.sleep_quality)
  }

  if (typeof entry.energy_level === 'number') {
    metrics.push(entry.energy_level)
  }

  if (typeof entry.mood_level === 'number') {
    metrics.push(entry.mood_level)
  }

  if (typeof entry.fatigue_level === 'number') {
    metrics.push(Math.max(0, 11 - entry.fatigue_level))
  }

  if (typeof entry.transit_quality === 'number') {
    metrics.push(entry.transit_quality)
  }

  if (typeof entry.appetite_level === 'number') {
    metrics.push(entry.appetite_level)
  }

  if (typeof entry.pain_level === 'number') {
    metrics.push(Math.max(0, 11 - entry.pain_level))
  }

  if (typeof entry.sleep_hours === 'number') {
    const sleepWindowScore = Math.max(0, 10 - Math.abs(entry.sleep_hours - 8) * 2)
    metrics.push(Math.min(10, Number(sleepWindowScore.toFixed(1))))
  }

  if (entry.bleeding_present) {
    metrics.push(2)
  }

  if (metrics.length === 0) {
    return null
  }

  const average = metrics.reduce((total, value) => total + value, 0) / metrics.length
  return Number(average.toFixed(1))
}

export const getHealthStatus = (score: number | null) => {
  if (score === null) {
    return {
      label: 'A completer',
      className: 'text-gray-300 bg-dark-surface-high border-dark-border',
    }
  }

  if (score >= 8) {
    return {
      label: 'Stable',
      className: 'text-green-300 bg-green-900/30 border-green-800/40',
    }
  }

  if (score >= 6) {
    return {
      label: 'A surveiller',
      className: 'text-yellow-300 bg-yellow-900/30 border-yellow-800/40',
    }
  }

  return {
    label: 'Prioritaire',
    className: 'text-red-300 bg-red-900/30 border-red-800/40',
  }
}

export async function fetchHealthEntries(ownerId: string, limit = 14) {
  const { data, error } = await supabase
    .from('health_entries')
    .select('*')
    .eq('owner_id', ownerId)
    .order('entry_date', { ascending: false })
    .limit(limit)

  if (error) {
    throw new Error(error.message)
  }

  return (data ?? []) as HealthEntry[]
}

export async function fetchHealthEntryByDate(ownerId: string, entryDate: string) {
  const { data, error } = await supabase
    .from('health_entries')
    .select('*')
    .eq('owner_id', ownerId)
    .eq('entry_date', entryDate)
    .maybeSingle()

  if (error) {
    throw new Error(error.message)
  }

  return (data as HealthEntry | null) ?? null
}

export async function saveHealthEntry(input: HealthEntryInput) {
  const payload = {
    owner_id: input.owner_id,
    entry_date: input.entry_date,
    summary: input.summary ?? null,
    voice_note: input.voice_note ?? null,
    pain_level: input.pain_level ?? null,
    sleep_hours: input.sleep_hours ?? null,
    sleep_quality: input.sleep_quality ?? null,
    energy_level: input.energy_level ?? null,
    mood_level: input.mood_level ?? null,
    fatigue_level: input.fatigue_level ?? null,
    transit_quality: input.transit_quality ?? null,
    appetite_level: input.appetite_level ?? null,
    stool_count: input.stool_count ?? null,
    bleeding_present: input.bleeding_present ?? false,
    hydration_liters: input.hydration_liters ?? null,
    temperature_c: input.temperature_c ?? null,
    weight_kg: input.weight_kg ?? null,
    symptoms: input.symptoms ?? [],
    triggers: input.triggers ?? [],
    medications_taken: input.medications_taken ?? [],
    notes: input.notes ?? null,
    urgent_alert: input.urgent_alert ?? false,
    metadata: input.metadata ?? {},
  }

  const { data, error } = await supabase
    .from('health_entries')
    .upsert(payload, { onConflict: 'owner_id,entry_date' })
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return data as HealthEntry
}
